/**
 * Chart series for the demo world.
 *
 * The hard constraints, all of which exist because the UI shows these numbers
 * next to each other and any disagreement reads as a bug:
 *
 *  1. the last close of every candle set EQUALS the token's live spot price;
 *  2. the series passes through the prices implied by `change1h` / `change24h` /
 *     `change7d`, so the chart and the delta badge tell the same story;
 *  3. the `floor` track is monotonically non-decreasing and never above the low.
 *
 * (3) is not cosmetic. `redeemAtFloor` is a standing bid nobody can cancel, so a
 * price below the floor is not a thing that can happen — which is why lows are
 * clamped UP to the floor rather than the floor being clamped down to the low.
 */

import { weiToNumber } from '@/lib/format';
import { curveStateFromToken, launchPrice, spotPrice } from './curve';
import { makeRng, rngForAddress, type Rng } from './rng';
import type { Candle, CandleInterval, DemoToken, ProtocolStats, SeriesPoint } from './types';

const MINUTE = 60_000;
const HOUR = 3_600_000;
const DAY = 86_400_000;

export const INTERVAL_MS: Record<CandleInterval, number> = {
  '1m': MINUTE,
  '5m': 5 * MINUTE,
  '15m': 15 * MINUTE,
  '1h': HOUR,
  '4h': 4 * HOUR,
  '1d': DAY,
};

/** Bucket counts chosen so every interval spans a sensible window: 3h, 15h,
 *  40h, 7d, 20d, 90d. */
export const INTERVAL_COUNT: Record<CandleInterval, number> = {
  '1m': 180,
  '5m': 180,
  '15m': 160,
  '1h': 168,
  '4h': 120,
  '1d': 90,
};

export interface PriceAnchor {
  t: number;
  p: number;
}

/** Guards `spot / (1 + change)` against a change of -100% or worse. */
function backOut(spot: number, change: number): number {
  return spot / Math.max(0.02, 1 + change);
}

/**
 * The skeleton every price series hangs on: launch price at `createdAt`, then the
 * prices implied by the token's own 7d / 24h / 1h deltas, then live spot.
 * Anchors older than the token are dropped, so a 9-minute-old token's chart does
 * not pretend to have a week of history.
 */
export function priceAnchors(token: DemoToken, now = Date.now()): PriceAnchor[] {
  const state = curveStateFromToken(token);
  const spot = spotPrice(state);
  const start = launchPrice(state) || spot * 0.05;

  const raw: PriceAnchor[] = [
    { t: token.createdAt, p: Math.min(start, spot) },
    { t: now - 7 * DAY, p: backOut(spot, token.change7d) },
    { t: now - DAY, p: backOut(spot, token.change24h) },
    { t: now - HOUR, p: backOut(spot, token.change1h) },
    { t: now, p: spot },
  ];

  const out: PriceAnchor[] = [];
  for (const a of raw) {
    if (a.t < token.createdAt || !(a.p > 0)) continue;
    const prev = out[out.length - 1];
    if (prev && a.t - prev.t < MINUTE) out.pop();
    out.push(a);
  }
  if (out.length === 0) out.push({ t: now, p: spot });
  if (out.length === 1) out.unshift({ t: Math.min(token.createdAt, now - MINUTE), p: Math.min(start, spot) });
  return out;
}

/** Log-linear interpolation between anchors, flat outside their range. */
export function anchorPriceAt(anchors: PriceAnchor[], t: number): number {
  const first = anchors[0]!;
  const last = anchors[anchors.length - 1]!;
  if (t <= first.t) return first.p;
  if (t >= last.t) return last.p;
  for (let i = 1; i < anchors.length; i++) {
    const a = anchors[i - 1]!;
    const b = anchors[i]!;
    if (t <= b.t) {
      const f = (t - a.t) / Math.max(1, b.t - a.t);
      // Interpolate in log space: percentage moves, not absolute ones, are what a
      // price series is actually made of.
      return Math.exp(Math.log(a.p) * (1 - f) + Math.log(b.p) * f);
    }
  }
  return last.p;
}

/**
 * A random walk in log space that is pinned to zero at `knots`. Pinning is what
 * lets the noise be genuinely random while the series still lands exactly on the
 * anchor prices — including the final bar, which must equal live spot.
 */
function pinnedWalk(rng: Rng, n: number, vol: number, knots: number[]): number[] {
  const walk = new Array<number>(n).fill(0);
  for (let i = 1; i < n; i++) {
    walk[i] = walk[i - 1]! + rng.gauss(0, vol) - walk[i - 1]! * 0.06;
  }
  const pins = Array.from(new Set([0, ...knots, n - 1]))
    .filter((i) => i >= 0 && i < n)
    .sort((a, b) => a - b);

  const out = new Array<number>(n).fill(0);
  for (let s = 0; s < pins.length - 1; s++) {
    const a = pins[s]!;
    const b = pins[s + 1]!;
    const wa = walk[a]!;
    const wb = walk[b]!;
    for (let i = a; i <= b; i++) {
      const f = b === a ? 0 : (i - a) / (b - a);
      out[i] = walk[i]! - (wa * (1 - f) + wb * f);
    }
  }
  return out;
}

/** Monotone non-decreasing ramp from `from` to `to` in `n` steps, lumpy on
 *  purpose: the floor moves in ratchets, not a smooth line. */
function monotoneRamp(rng: Rng, n: number, from: number, to: number): number[] {
  if (n <= 1) return [to];
  const inc = new Array<number>(n).fill(0);
  let total = 0;
  for (let i = 1; i < n; i++) {
    // Squaring the draw makes most steps tiny and a few of them jumps.
    const step = Math.pow(rng.rand(), 2.2) + 0.02;
    inc[i] = step;
    total += step;
  }
  const span = Math.max(0, to - from);
  const out = new Array<number>(n).fill(from);
  let acc = 0;
  for (let i = 1; i < n; i++) {
    acc += inc[i]!;
    out[i] = from + (span * acc) / (total || 1);
  }
  out[n - 1] = to;
  return out;
}

/* ────────────────────────────────────────────────────────────────────────────
   Candles
   ──────────────────────────────────────────────────────────────────────────── */

const candleCache = new Map<string, Candle[]>();
const sparkCache = new Map<string, { price: number[]; floor: number[] }>();
const floorLockedCache = new Map<string, SeriesPoint[]>();

/** Called when the world is regenerated, so stale series cannot leak across it. */
export function clearSeriesCache(): void {
  candleCache.clear();
  sparkCache.clear();
  floorLockedCache.clear();
}

/** Drops the cached series for one token, e.g. after a trade moves its price. */
export function invalidateSeries(address: string): void {
  const key = address.toLowerCase();
  for (const k of [...candleCache.keys()]) if (k.startsWith(key)) candleCache.delete(k);
  for (const k of [...sparkCache.keys()]) if (k.startsWith(key)) sparkCache.delete(k);
}

export function buildCandles(token: DemoToken, interval: CandleInterval, count?: number): Candle[] {
  const key = `${token.address.toLowerCase()}:${interval}:${count ?? 'default'}`;
  const hit = candleCache.get(key);
  if (hit) return hit;

  const bucket = INTERVAL_MS[interval];
  const want = count ?? INTERVAL_COUNT[interval];
  const now = Date.now();
  const state = curveStateFromToken(token);
  const spot = spotPrice(state);
  const floorNow = weiToNumber(token.floorPrice);

  const lastOpen = Math.floor(now / bucket) * bucket;
  // Young tokens only get bars that actually postdate their launch.
  const firstPossible = Math.floor(token.createdAt / bucket) * bucket;
  const n = Math.max(1, Math.min(want, Math.floor((lastOpen - firstPossible) / bucket) + 1));
  const firstOpen = lastOpen - (n - 1) * bucket;

  const anchors = priceAnchors(token, now);
  const rng = rngForAddress(token.address, `candles:${interval}:${n}`);

  // Knot the noise at the bars holding the 24h/7d/1h anchors so the deltas the
  // badge shows are actually visible on the chart.
  const knots = anchors.map((a) => Math.round((a.t - firstOpen) / bucket)).filter((i) => i > 0 && i < n - 1);
  const vol = interval === '1d' ? 0.055 : interval === '4h' ? 0.04 : interval === '1h' ? 0.028 : 0.016;
  const noise = pinnedWalk(rng, n, vol, knots);

  // The floor at the start of the window: older windows reach further back into
  // the token's life, so they start proportionally lower.
  const spanDays = (n * bucket) / DAY;
  const ageDays = Math.max(0.01, (now - token.createdAt) / DAY);
  const floorStart = floorNow * Math.max(0.04, 1 - 0.9 * Math.min(1, spanDays / ageDays));
  const floorTrack = monotoneRamp(rng.fork('floor'), n, floorStart, floorNow);

  const barsPerDay = Math.max(1, DAY / bucket);
  const baseVol = token.volume24hEth / barsPerDay;

  const out: Candle[] = [];
  let prevClose = 0;
  for (let i = 0; i < n; i++) {
    const t = firstOpen + i * bucket;
    const closeAt = Math.min(now, t + bucket);
    const fl = floorTrack[i]!;

    let c = anchorPriceAt(anchors, closeAt) * Math.exp(noise[i]!);
    if (i === n - 1) c = spot; // invariant 1: the chart ends where the ticker is
    let o = i === 0 ? anchorPriceAt(anchors, t) * Math.exp(noise[0]!) : prevClose;

    // Invariant 3: nothing trades below the standing redeem bid.
    o = Math.max(o, fl);
    c = Math.max(c, fl);

    const wickUp = Math.abs(rng.gauss(0, vol * 0.9));
    const wickDown = Math.abs(rng.gauss(0, vol * 0.9));
    const h = Math.max(o, c) * (1 + wickUp);
    const l = Math.max(fl, Math.min(o, c) * (1 - wickDown));

    out.push({
      t,
      o,
      h,
      l,
      c,
      v: Math.max(0, baseVol * (0.15 + Math.pow(rng.rand(), 2) * 3.2)),
      floor: fl,
    });
    prevClose = c;
  }

  candleCache.set(key, out);
  return out;
}

/* ────────────────────────────────────────────────────────────────────────────
   Sparklines
   ──────────────────────────────────────────────────────────────────────────── */

const SPARK_POINTS = 48;

/** Price and floor tracks are built together so the two arrays a `Sparkline`
 *  receives can never contradict each other. */
function buildSpark(token: DemoToken): { price: number[]; floor: number[] } {
  const key = `${token.address.toLowerCase()}:spark`;
  const hit = sparkCache.get(key);
  if (hit) return hit;

  const now = Date.now();
  const state = curveStateFromToken(token);
  const spot = spotPrice(state);
  const floorNow = weiToNumber(token.floorPrice);
  const anchors = priceAnchors(token, now);
  const rng = rngForAddress(token.address, 'spark');

  const step = DAY / (SPARK_POINTS - 1);
  const start = Math.max(token.createdAt, now - DAY);
  const noise = pinnedWalk(rng, SPARK_POINTS, 0.018, []);
  const ageDays = Math.max(0.01, (now - token.createdAt) / DAY);
  const floorTrack = monotoneRamp(
    rng.fork('spark-floor'),
    SPARK_POINTS,
    floorNow * Math.max(0.05, 1 - 0.85 * Math.min(1, 1 / ageDays)),
    floorNow,
  );

  const price: number[] = [];
  for (let i = 0; i < SPARK_POINTS; i++) {
    const t = start + i * ((now - start) / (SPARK_POINTS - 1) || step);
    const v = i === SPARK_POINTS - 1 ? spot : anchorPriceAt(anchors, t) * Math.exp(noise[i]!);
    price.push(Math.max(v, floorTrack[i]!));
  }

  const value = { price, floor: floorTrack };
  sparkCache.set(key, value);
  return value;
}

export function buildSparkline(token: DemoToken): number[] {
  return buildSpark(token).price;
}

export function buildFloorSparkline(token: DemoToken): number[] {
  return buildSpark(token).floor;
}

/* ────────────────────────────────────────────────────────────────────────────
   Protocol-wide floor locked
   ──────────────────────────────────────────────────────────────────────────── */

/**
 * 90 daily points ending at the live total. Monotone by construction: the floor
 * reserve has no withdrawal path, so protocol-wide locked ETH cannot fall (a
 * redeem returns it to a holder, which the demo folds into `ethReturnedEth`).
 */
export function buildFloorLockedSeries(stats: ProtocolStats, days = 90): SeriesPoint[] {
  const key = `locked:${days}:${stats.floorLockedEth.toFixed(6)}`;
  const hit = floorLockedCache.get(key);
  if (hit) return hit;

  const rng = makeRng(`floor-locked:${days}`);
  const now = Date.now();
  const today = Math.floor(now / DAY) * DAY;
  const ramp = monotoneRamp(rng, days, stats.floorLockedEth * 0.06, stats.floorLockedEth);

  const out: SeriesPoint[] = ramp.map((v, i) => ({ t: today - (days - 1 - i) * DAY, v }));
  floorLockedCache.set(key, out);
  return out;
}
